export const CloverCnpTokenSaleOutcome = {
    APPROVED: 'approved',
    CARD_DECLINED: 'card_declined',
    DECLINED: 'declined',
    INVALID_SOURCE: 'invalid_source',
    MALFORMED_RESPONSE: 'malformed_response',
} as const;

export type CloverCnpTokenSaleOutcome =
    (typeof CloverCnpTokenSaleOutcome)[keyof typeof CloverCnpTokenSaleOutcome];

export const CloverCnpTokenSaleStatus = {
    SUCCEEDED: 'succeeded',
    PENDING: 'pending',
    FAILED: 'failed',
} as const;

export type CloverCnpTokenSaleStatus =
    (typeof CloverCnpTokenSaleStatus)[keyof typeof CloverCnpTokenSaleStatus];

export const CloverCnpTokenSaleNetworkStatus = {
    APPROVED_BY_NETWORK: 'approved_by_network',
    DECLINED_BY_NETWORK: 'declined_by_network',
    NOT_SENT_TO_NETWORK: 'not_sent_to_network',
    REVERSED_AFTER_APPROVAL: 'reversed_after_approval',
} as const;

export type CloverCnpTokenSaleNetworkStatus =
    (typeof CloverCnpTokenSaleNetworkStatus)[keyof typeof CloverCnpTokenSaleNetworkStatus];

export const CloverCnpTokenSaleOutcomeType = {
    AUTHORIZED: 'authorized',
    BLOCKED: 'blocked',
    INVALID: 'invalid',
    ISSUER_DECLINED: 'issuer_declined',
    MANUAL_REVIEW: 'manual_review',
} as const;

export type CloverCnpTokenSaleOutcomeType =
    (typeof CloverCnpTokenSaleOutcomeType)[keyof typeof CloverCnpTokenSaleOutcomeType];

export interface CloverCnpTokenSaleRequestHeaders {
    readonly userAgent: string;
    readonly forwardedFor: string;
}

export interface CloverCnpTokenSaleFirstStoredCredentials {
    readonly sequence: 'FIRST';
    readonly is_scheduled: false;
    readonly initiator: 'CARDHOLDER';
}

export interface CloverCnpTokenSaleSubsequentStoredCredentials {
    readonly sequence: 'SUBSEQUENT';
    readonly is_scheduled?: boolean;
    readonly initiator?: 'MERCHANT' | 'CARDHOLDER';
}

export type CloverCnpTokenSaleStoredCredentials =
    | CloverCnpTokenSaleFirstStoredCredentials
    | CloverCnpTokenSaleSubsequentStoredCredentials;

export interface CloverCnpTokenSaleRequestBody {
    readonly amount: number;
    readonly currency: string;
    readonly source: string;
    readonly intent?: 'save_credential_on_file';
    readonly capture?: boolean;
    readonly description?: string;
    readonly external_reference_id?: string;
    readonly receipt_email?: string;
    readonly stored_credentials?: CloverCnpTokenSaleStoredCredentials;
}

export interface CloverCnpTokenSaleRequestDetails {
    readonly method: 'POST';
    readonly path: '/v1/charges';
    readonly headers: CloverCnpTokenSaleRequestHeaders;
    readonly body: CloverCnpTokenSaleRequestBody;
}

export interface CloverCnpTokenSaleChargeOutcomeBody {
    readonly network_status?: CloverCnpTokenSaleNetworkStatus;
    readonly reason?: string | null;
    readonly seller_message?: string;
    readonly type?: CloverCnpTokenSaleOutcomeType;
}

export interface CloverCnpTokenSaleSourceBody {
    readonly id?: string;
    readonly object?: string;
    readonly brand?: string | null;
    readonly exp_month?: string;
    readonly exp_year?: string;
    readonly first6?: string;
    readonly funding?: string;
    readonly last4?: string;
    readonly name?: string;
}

export const CloverCnpTokenSaleAdditionalChargeType = {
    CREDIT_SURCHARGE: 'CREDIT_SURCHARGE',
} as const;

export type CloverCnpTokenSaleAdditionalChargeType =
    (typeof CloverCnpTokenSaleAdditionalChargeType)[keyof typeof CloverCnpTokenSaleAdditionalChargeType];

export interface CloverCnpTokenSaleAdditionalCharge {
    readonly amount: number;
    readonly id?: string;
    readonly rate?: number;
    readonly type: typeof CloverCnpTokenSaleAdditionalChargeType.CREDIT_SURCHARGE;
}

export interface CloverCnpTokenSaleChargeBody {
    readonly id: string;
    readonly amount?: number;
    readonly additional_charges?: CloverCnpTokenSaleAdditionalCharge;
    readonly amount_refunded?: number;
    readonly auth_code?: string | null;
    readonly captured: boolean;
    readonly created?: number;
    readonly currency: string;
    readonly description?: string | null;
    readonly external_reference_id?: string;
    readonly failure_code?: string | null;
    readonly failure_message?: string | null;
    readonly outcome?: CloverCnpTokenSaleChargeOutcomeBody;
    readonly paid: boolean;
    readonly receipt_email?: string | null;
    readonly ref_num?: string | null;
    readonly refunded?: boolean;
    readonly source?: CloverCnpTokenSaleSourceBody;
    readonly status: CloverCnpTokenSaleStatus;
}

export interface CloverCnpTokenSaleApprovedChargeBody extends CloverCnpTokenSaleChargeBody {
    readonly captured: true;
    readonly paid: true;
    readonly status: typeof CloverCnpTokenSaleStatus.SUCCEEDED;
}

export interface CloverCnpTokenSaleDeclinedChargeBody extends CloverCnpTokenSaleChargeBody {
    readonly paid: false;
    readonly status: typeof CloverCnpTokenSaleStatus.FAILED;
}

export interface CloverCnpTokenSaleApprovedResponseDetails {
    readonly outcome: typeof CloverCnpTokenSaleOutcome.APPROVED;
    readonly httpStatus: 200;
    readonly body: CloverCnpTokenSaleApprovedChargeBody;
}

export interface CloverCnpTokenSaleDeclinedResponseDetails {
    readonly outcome: typeof CloverCnpTokenSaleOutcome.DECLINED;
    readonly httpStatus: 200;
    readonly body: CloverCnpTokenSaleDeclinedChargeBody;
}

export interface CloverCnpTokenSaleCardDeclinedBody {
    readonly error: {
        readonly code: 'card_declined';
        readonly charge: string;
        readonly message: string;
        readonly declineCode: typeof CloverCnpTokenSaleOutcomeType.ISSUER_DECLINED;
    };
    readonly message: '402 Payment Required';
}

export interface CloverCnpTokenSaleCardDeclinedResponseDetails {
    readonly outcome: typeof CloverCnpTokenSaleOutcome.CARD_DECLINED;
    readonly httpStatus: 402;
    readonly body: CloverCnpTokenSaleCardDeclinedBody;
}

export interface CloverCnpTokenSaleInvalidSourceBody {
    readonly error: {
        readonly code: 'processing_error';
        readonly message: 'Invalid Request. Pay request sent for stored credential transaction with invalid source.';
    };
    readonly message: '400 Bad Request';
}

export interface CloverCnpTokenSaleInvalidSourceResponseDetails {
    readonly outcome: typeof CloverCnpTokenSaleOutcome.INVALID_SOURCE;
    readonly httpStatus: 400;
    readonly body: CloverCnpTokenSaleInvalidSourceBody;
}

export interface CloverCnpTokenSaleMalformedResponseDetails {
    readonly outcome: typeof CloverCnpTokenSaleOutcome.MALFORMED_RESPONSE;
    readonly httpStatus: number;
    readonly body: unknown;
    readonly message: string;
}

export type CloverCnpTokenSaleResponseDetails =
    | CloverCnpTokenSaleApprovedResponseDetails
    | CloverCnpTokenSaleCardDeclinedResponseDetails
    | CloverCnpTokenSaleDeclinedResponseDetails
    | CloverCnpTokenSaleInvalidSourceResponseDetails
    | CloverCnpTokenSaleMalformedResponseDetails;
